import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, File, Layers, ListChecks, Loader2, Plus } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useResponsesStore } from '@/stores/responses-store'
import { cn } from '@/lib/utils'
import type { QuestionnaireItem } from '@/types/questionnaire-types'

interface ResponsePickerPopoverProps {
  bidId: string
  onSelect: (responseId: string, sectionKey: string | null) => void | Promise<void>
  onCreate: () => void | Promise<void>
}

function getSectionLabel(
  artifact: { artifactType: string },
  section: { title?: string; sectionKey: string },
  index: number
): string {
  if (artifact.artifactType === 'questionnaire') {
    const q = section as unknown as QuestionnaireItem
    const prefix = q.number ? `${q.number}. ` : ''
    return `${prefix}${q.prompt || 'Untitled question'}`
  }
  return `${index + 1}. ${section.title || 'Untitled section'}`
}

export function ResponsePickerPopover({
  bidId,
  onSelect,
  onCreate,
}: ResponsePickerPopoverProps) {
  const [search, setSearch] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const artifacts = useResponsesStore((state) => state.artifacts)
  const isLoading = useResponsesStore((state) => state.isLoading)
  const ensureArtifacts = useResponsesStore((state) => state.ensureArtifacts)

  useEffect(() => {
    void ensureArtifacts(bidId)
  }, [bidId, ensureArtifacts])

  const filteredArtifacts = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return artifacts

    return artifacts.filter((artifact) => {
      if (artifact.name.toLowerCase().includes(query)) return true
      const sections = artifact.outlineJson?.sections ?? []
      return sections.some((section, index) => {
        const label = getSectionLabel(artifact, section, index)
        return label.toLowerCase().includes(query)
      })
    })
  }, [artifacts, search])

  return (
    <div className="w-80">
      <div className="border-b p-2">
        <Input
          placeholder="Search responses..."
          className="h-8 text-sm"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="max-h-64 overflow-y-auto py-1">
        {isLoading && artifacts.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading responses...
          </div>
        ) : filteredArtifacts.length === 0 ? (
          <div className="px-3 py-3 text-sm text-muted-foreground">
            No matching responses yet.
          </div>
        ) : (
          filteredArtifacts.map((artifact) => {
            const sections = artifact.outlineJson?.sections ?? []
            const isExpandable = artifact.scopeKind !== 'artifact' && sections.length > 0
            const isExpanded = expandedIds.has(artifact.id)

            return (
              <div key={artifact.id} className="px-1">
                <div className="flex w-full items-center gap-0 rounded-md hover:bg-muted">
                  <button
                    type="button"
                    className="flex flex-1 items-center gap-2 px-2 py-2 text-left text-sm min-w-0"
                    onClick={() => void onSelect(artifact.id, null)}
                  >
                    {artifact.artifactType === 'questionnaire' ? (
                      <ListChecks className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : artifact.scopeKind === 'artifact' ? (
                      <File className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="flex-1 truncate">{artifact.name}</span>
                  </button>
                  {isExpandable && (
                    <button
                      type="button"
                      className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                      onClick={() => {
                        setExpandedIds((prev) => {
                          const next = new Set(prev)
                          if (next.has(artifact.id)) next.delete(artifact.id)
                          else next.add(artifact.id)
                          return next
                        })
                      }}
                    >
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 transition-transform',
                          isExpanded && 'rotate-180'
                        )}
                      />
                    </button>
                  )}
                </div>

                {isExpandable && isExpanded && (
                  <div className="mb-1 ml-6 space-y-1">
                    {sections.map((section, index) => (
                      <button
                        key={section.sectionKey}
                        type="button"
                        className="flex w-full items-center rounded-md px-2 py-1.5 text-left text-meta hover:bg-muted hover:text-foreground"
                        onClick={() => void onSelect(artifact.id, section.sectionKey)}
                      >
                        <span className="truncate">
                          {getSectionLabel(artifact, section, index)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      <div className="border-t p-2">
        <Button
          type="button"
          variant="ghost"
          className="w-full justify-start gap-2 px-2"
          onClick={() => void onCreate()}
        >
          <Plus className="h-4 w-4" />
          Create new response
        </Button>
      </div>
    </div>
  )
}
